<template>
    <div
        class="relative p-0"
        :class="calculatedWidth ? 'w-fit' : 'w-full'"
    >
        <div
            v-if="!editing"
            class="flex flex-col"
        >
            <span class="text-base font-light text-gray-500">{{ label }}</span>
            {{ inputValue }}
        </div>
        <div
            v-else-if="editing"
            class="relative flex w-full cursor-pointer select-none"
        >
            <input
                :id="inputId"
                ref="input"
                :data-cy="vid"
                :disabled="disabled"
                :name="vid"
                :type="inputType"
                class="peer rounded-muval-1 focus:border-focus block w-full resize-y overflow-y-auto border px-3.5 pl-12 text-base font-normal placeholder-transparent focus:ring-0 focus:outline-hidden"
                :class="[getStyles]"
                :size="width"
                :placeholder="placeholder ? placeholder : label"
                :value="inputValue"
                :autocomplete="canAutoComplete"
                @input="handleChange"
                @blur="handleBlur"
            />
            <div
                v-if="label"
                class="peer text-muval-gray-3 peer-placeholder-shown:text-muval-gray-3 peer-focus:text-focus pointer-events-none absolute left-2 z-10 line-clamp-1 -translate-y-1/2 text-sm transition-all duration-100 peer-placeholder-shown:top-1/2 peer-placeholder-shown:left-10 peer-placeholder-shown:text-base peer-focus:top-0 peer-focus:left-2 peer-focus:text-sm"
            >
                <span class="px-1">{{ label }}</span>
                <div
                    class="absolute top-2.5 -z-10 h-0.5 w-full"
                    :class="getStyles"
                ></div>
            </div>

            <div
                class="text-muval-gray-3 peer-focus:text-muval-gray-2 pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
            >
                <template v-if="cubic">
                    <span>
                        m
                        <sup>3</sup>
                    </span>
                </template>
                <template v-if="infoType">
                    <span>{{ infoType }}</span>
                </template>
                <template v-if="errorMessage">
                    <SvgIcon
                        class="h-4 w-4"
                        name="input_error"
                    />
                </template>
            </div>
        </div>
        <div
            class="pointer-events-none absolute -bottom-3.5 left-0 w-full truncate text-xs whitespace-nowrap"
            :class="errorMessage ? 'text-red-500' : ''"
        >
            <span class="ml-2 text-xs">{{ errorMessage }}</span>
        </div>
    </div>
</template>

<script setup>
import { useField } from 'vee-validate';
import { idGen } from '@/utilities/idGen';
import SvgIcon from '../utilities/SvgIcon.vue';

const props = defineProps({
    label: {
        type: [String, null],
        required: false,
        default: null,
    },
    placeholder: {
        type: [String, null],
        required: false,
        default: null,
    },
    disabled: {
        type: Boolean,
        required: false,
        default: false,
    },
    vid: {
        type: String,
        required: false,
        default: () => idGen(6),
    },
    updateOn: {
        type: String,
        required: false,
        default: 'blur',
    },
    inputType: {
        type: String,
        required: false,
        default: 'text',
    },
    cubic: {
        type: Boolean,
        required: false,
        default: false,
    },
    editing: {
        type: Boolean,
        required: false,
        default: true,
    },
    infoType: {
        type: [String, null],
        required: false,
        default: null,
    },
    calculatedWidth: {
        type: Boolean,
        required: false,
        default: null,
    },
    autocomplete: {
        type: Boolean,
        required: false,
        default: true,
    },
});

const emit = defineEmits(['update:modelValue']);

const inputId = idGen(6);
const { value: inputValue, errorMessage, handleBlur, handleChange } = useField(props.vid, undefined);

const canAutoComplete = computed(() => {
    if (props.autocomplete) {
        return 'on';
    } else {
        return 'off';
    }
});

watch(inputValue, (newVal) => {
    if (newVal === props.modelValue) {
        return;
    }
    // sync the model value with vee-validate model
    emit('update:modelValue', newVal);
});

// and you need to listen for `modelValue` prop changes
watch(
    () => props.modelValue,
    (newModel) => {
        if (newModel === inputValue.value) {
            return;
        }

        // Sync the vee-validate model with model value
        inputValue.value = newModel;
    },
);

const width = computed(() => {
    if (props.calculatedWidth) {
        let width = props.infoType?.length ? props.infoType.length : 0;
        if (props.placeholder) {
            return props.placeholder.length + width;
        } else if (props.label) {
            return props.label.length + width;
        } else {
            return 100;
        }
    }
    return null;
});

const getStyles = computed(() => {
    if (props.disabled) {
        return 'bg-muval-gray-5 border-muval-gray-4 cursor-not-allowed';
    } else if (errorMessage.value) {
        return 'bg-white border-error';
    } else {
        return 'bg-white border-muval-gray-4';
    }
});
</script>

<style scoped>
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
    transition: background-color 5000s ease-in-out 0s;
}
</style>
